import type React from 'react';
import { useState, useCallback, useRef } from 'react';
import { getCryptoIconUrl, getFiatFlagUrl } from '../../data/cryptoData';

/* ───────────── icon helpers ───────────── */

/** Lazy-loaded image with placeholder and fallback */
function LazyIcon({
  src,
  alt,
  size,
  className = '',
  background,
}: {
  src: string;
  alt: string;
  size: number;
  className?: string;
  /** Background color for transparent icons */
  background?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const currentSrc = useRef(src);

  // Reset state when src changes, using ref to avoid race with onLoad
  if (currentSrc.current !== src) {
    currentSrc.current = src;
    setLoaded(false);
    setError(false);
  }

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => setError(true), []);

  return (
    <div
      className={`relative rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size, background }}
    >
      {/* Placeholder while loading */}
      {!loaded && !error && (
        <div
          className="absolute inset-0 rounded-full animate-pulse"
          style={{ background: 'var(--t-pill-bg, #333)' }}
        />
      )}
      {/* Fallback for broken images */}
      {error ? (
        <div
          className="absolute inset-0 rounded-full flex items-center justify-center font-bold"
          style={{
            background: 'var(--t-pill-bg, #333)',
            color: 'var(--t-text-muted)',
            fontSize: Math.max(8, size * 0.35),
          }}
        >
          {alt.slice(0, 3)}
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          onLoad={handleLoad}
          onError={handleError}
          className={`rounded-full ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ width: size, height: size, transition: 'opacity 0.15s ease-in' }}
        />
      )}
    </div>
  );
}

export function CryptoIcon({ ticker, size = 32, baseUrl = '' }: { ticker: string; size?: number; baseUrl?: string }) {
  return (
    <LazyIcon
      src={getCryptoIconUrl(ticker, baseUrl)}
      alt={ticker}
      size={size}
      background="#ffffff"
    />
  );
}

export function FiatFlag({ currencyCode, size = 16, baseUrl = '' }: { currencyCode: string; size?: number; baseUrl?: string }) {
  return (
    <LazyIcon
      src={getFiatFlagUrl(currencyCode, baseUrl)}
      alt={currencyCode}
      size={size}
    />
  );
}

export function GenericCoinIcon({ size = 32, color = '#888' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill={color} />
      <circle cx="16" cy="16" r="8" stroke="white" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

export function BankIcon({ size = 24 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-card-icon-bg)' }}
    >
      <svg width={size * 0.6} height={size * 0.58} viewBox="0 0 25 24" fill="none">
        <path d="M23.292 7.5H1.70801L12.5 0.592773L23.292 7.5Z" stroke="var(--t-card-icon-stroke)" strokeWidth="1" />
        <rect x="3" y="10" width="1.5" height="11" fill="var(--t-card-icon-stroke)" />
        <rect x="9" y="10" width="1.5" height="11" fill="var(--t-card-icon-stroke)" />
        <rect x="15" y="10" width="1.5" height="11" fill="var(--t-card-icon-stroke)" />
        <rect x="21" y="10" width="1.5" height="11" fill="var(--t-card-icon-stroke)" />
        <rect y="23" width="25" height="1" fill="var(--t-card-icon-stroke)" />
      </svg>
    </div>
  );
}

export function RevolutIcon({ size = 32 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold shrink-0"
      style={{ width: size, height: size, backgroundColor: '#333', fontSize: size * 0.45 }}
    >
      R
    </div>
  );
}

export function ApplePayIcon({ size = 32 }: { size?: number }) {
  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: '#000', borderRadius: size * 0.2 }}
    >
      <svg width={size * 0.72} height={size * 0.288} viewBox="0 0 50 20" fill="none">
        <path
          d="M9.14529 2.57901C8.55528 3.25398 7.61984 3.7887 6.68351 3.70976C6.56186 2.7971 7.02999 1.82591 7.55883 1.23105C8.14846 0.535892 9.166 0.0403839 9.9999 0C10.1017 0.952461 9.71518 1.88488 9.14529 2.57901ZM9.98932 3.88805C10.5171 3.92756 12.0384 4.08587 13.0125 5.48951C12.9312 5.54899 11.2066 6.51813 11.2267 8.55562C11.247 10.9883 13.4176 11.7995 13.4379 11.8193C13.4176 11.8787 13.0939 12.9667 12.3023 14.0939C11.6126 15.083 10.902 16.052 9.76606 16.0717C8.67023 16.0915 8.30483 15.4389 7.04742 15.4389C5.78949 15.4389 5.38394 16.052 4.34875 16.0915C3.25329 16.1307 2.42185 15.0429 1.73195 14.054C0.310872 12.0564 -0.764097 8.41705 0.696758 5.96431C1.40722 4.73809 2.70553 3.96677 4.10507 3.94695C5.18064 3.92756 6.17501 4.63962 6.82446 4.63962C7.47376 4.63962 8.62918 3.80889 9.98932 3.88805ZM23.544 1.13133C26.4989 1.13133 28.5569 3.11593 28.5569 6.00514C28.5569 8.90473 26.4567 10.8997 23.4702 10.8997H20.1987V15.9694H17.8349V1.13133H23.544ZM20.1987 8.96648H22.9108C24.9692 8.96648 26.1402 7.88694 26.1402 6.01552C26.1402 4.14448 24.9688 3.07467 22.9212 3.07467H20.1987V8.96648ZM29.1434 12.8945C29.1434 10.9923 30.6314 9.90226 33.375 9.7378L36.3194 9.56295V8.74027C36.3194 7.53753 35.5071 6.87939 34.0613 6.87939C32.8683 6.87939 32.0028 7.47564 31.8242 8.39042H29.6919C29.7556 6.46802 31.6126 5.06972 34.1246 5.06972C36.8262 5.06972 38.5886 6.44746 38.5886 8.58634V15.969H36.4042V14.1901H36.3515C35.7287 15.3518 34.3565 16.0817 32.8683 16.0817C30.6734 16.0817 29.1434 14.8072 29.1434 12.8945ZM36.3195 11.9385V11.1054L33.6918 11.27C32.2138 11.3624 31.4442 11.897 31.4442 12.8325C31.4442 13.7375 32.2459 14.3238 33.5018 14.3238C35.1055 14.3238 36.3195 13.3262 36.3195 11.9385ZM40.597 19.9377V18.1592C40.7449 18.1794 41.1032 18.1999 41.2935 18.1999C42.3381 18.1999 42.9291 17.7688 43.288 16.6575L43.4992 15.9997L39.4992 5.20338H41.9686L44.7548 13.9638H44.8077L47.5936 5.20338H50L45.8524 16.5445C44.9024 19.1463 43.8155 20 41.515 20C41.3354 20 40.7549 19.9793 40.597 19.9377Z"
          fill="white"
        />
      </svg>
    </div>
  );
}

export function GooglePayIcon({ size = 32 }: { size?: number }) {
  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: '#3C4043', borderRadius: size * 0.2 }}
    >
      <svg width={size * 0.72} height={size * 0.296} viewBox="0 0 51 21" fill="none">
        <path d="M50.9156 5.87122L44.6406 20.2837H42.6992L45.0289 15.2416L40.9009 5.87122H42.9451L45.929 13.0573H45.9689L48.8714 5.87122H50.9156Z" fill="white" />
        <path d="M39.3906 6.65832C38.5866 5.91563 37.4895 5.54547 36.0995 5.54547C34.314 5.54547 32.9657 6.19816 32.0513 7.50532L33.7084 8.54664C34.3211 7.66089 35.1512 7.21951 36.2011 7.21951C36.8967 7.21951 37.5008 7.44661 38.0194 7.9023C38.538 8.35948 38.7964 8.91352 38.7964 9.5665V9.99507C38.0751 9.58796 37.1536 9.38381 36.0366 9.38381C34.7297 9.38381 33.6839 9.68959 32.8998 10.3023C32.1172 10.9151 31.7256 11.738 31.7256 12.7706C31.7256 13.8033 32.0969 14.5675 32.8396 15.2216C33.5808 15.8758 34.5065 16.2016 35.6092 16.2016C36.9033 16.2016 37.9389 15.6302 38.7147 14.4876H38.7976V15.8761H40.5944V9.70955C40.5944 8.4167 40.1944 7.39982 39.3903 6.65862L39.3906 6.65832ZM37.8665 13.6501C37.2451 14.2343 36.5424 14.5272 35.7511 14.5272C35.1926 14.5272 34.7068 14.3645 34.2913 14.0387C33.8741 13.7103 33.6673 13.3032 33.6673 12.8132C33.6673 12.2675 33.923 11.8134 34.4344 11.4462C34.9443 11.079 35.5815 10.8948 36.3442 10.8948C37.3926 10.8948 38.211 11.1249 38.7967 11.5889C38.7967 12.3775 38.4867 13.0645 37.8668 13.6501H37.8665Z" fill="white" />
        <path d="M30.4166 2.52282C29.5296 1.67999 28.4552 1.25739 27.1883 1.25739H22.2017V15.8758H24.0813V9.95362H27.188C28.4678 9.95362 29.5448 9.53936 30.4163 8.70815C31.3175 7.87962 31.7661 6.84397 31.7661 5.60565C31.7661 4.36733 31.3175 3.36744 30.4163 2.52312L30.4166 2.52282ZM29.0895 7.40248C28.598 7.90525 27.9781 8.15798 27.2297 8.15798H24.0816V3.05272H27.2297C27.9781 3.05272 28.5983 3.31141 29.0895 3.82849C29.5937 4.33276 29.845 4.92554 29.845 5.60535C29.845 6.28516 29.5934 6.8994 29.0895 7.40218V7.40248Z" fill="white" />
        <path d="M16.2663 11.4026C15.8887 12.6964 15.1901 13.9058 14.2131 14.8354C14.1815 14.8655 14.1496 14.895 14.1178 14.9245C14.116 14.9263 14.1148 14.9277 14.113 14.9292C13.1524 15.8144 11.9487 16.4295 10.6737 16.7272C10.6621 16.7302 10.6502 16.7329 10.6385 16.7356C10.2484 16.8241 9.85204 16.8888 9.45357 16.929C8.81668 16.9928 8.17293 16.9928 7.53723 16.9222C7.22102 16.8873 6.9063 16.8348 6.59546 16.7639C6.49293 16.7407 6.39101 16.7156 6.28938 16.6882C5.16759 16.3884 4.11256 15.8564 3.20476 15.1337C3.0018 14.971 2.80599 14.7987 2.61823 14.6184C2.61257 14.613 2.60721 14.6077 2.60154 14.6026C1.93962 13.9657 1.38081 13.22 0.956413 12.4034C0.938829 12.3691 0.921246 12.3352 0.90396 12.3009C0.717393 11.9319 0.559138 11.5493 0.429793 11.1574C0.364822 10.9618 0.307302 10.7634 0.257233 10.5631C0.226834 10.443 0.199117 10.3226 0.174678 10.2013C0.141299 10.0397 0.112986 9.87702 0.0894411 9.714C-0.0509314 8.75165 -0.0267907 7.76517 0.166929 6.81118C0.176168 6.76468 0.185705 6.71879 0.195838 6.6723C0.345748 5.97997 0.582981 5.31 0.903662 4.67698C1.53102 3.42525 2.48025 2.33983 3.62498 1.53514H3.62528C3.73704 1.45616 3.85029 1.38076 3.96533 1.30775C3.99424 1.28957 4.02345 1.27169 4.05206 1.25351C4.11018 1.21774 4.16889 1.18257 4.2276 1.148C4.34562 1.07945 4.46513 1.01329 4.58643 0.950111C4.64723 0.918519 4.70773 0.887822 4.76912 0.858018C5.0755 0.707811 5.39111 0.576975 5.71418 0.466108C5.77021 0.446438 5.82624 0.427961 5.88227 0.409781C5.96661 0.38266 6.05095 0.356731 6.13619 0.332292C6.19312 0.315603 6.25064 0.300106 6.30786 0.284906C6.36448 0.269707 6.4217 0.255401 6.47863 0.241393C6.51111 0.233346 6.5433 0.225895 6.57579 0.218146C6.62764 0.206225 6.6792 0.194901 6.73136 0.183874C6.73464 0.183278 6.73762 0.182681 6.7406 0.181787C6.90481 0.147216 7.06992 0.117711 7.23593 0.093273C7.25262 0.0905907 7.2693 0.088205 7.28629 0.0861187C8.67482 -0.111774 10.114 0.0309834 11.4319 0.518561C12.4443 0.889311 13.3712 1.47882 14.1651 2.21048C13.899 2.4957 13.6138 2.76661 13.3378 3.04705C12.8151 3.56533 12.2971 4.08807 11.774 4.60605C11.7734 4.60545 11.7723 4.60486 11.7717 4.60367L11.7645 4.61082C11.2465 4.11639 10.6192 3.74564 9.9346 3.53642C9.1314 3.29412 8.26652 3.26044 7.44425 3.43658C7.14741 3.49946 6.85772 3.59066 6.57847 3.70779C6.57519 3.70898 6.57221 3.71047 6.56893 3.71166C5.95231 3.97393 5.38843 4.36047 4.92022 4.83881C4.3638 5.39941 3.94089 6.09352 3.68906 6.83979C3.68727 6.8386 3.68548 6.83711 3.68369 6.83591C3.31831 7.89571 3.32308 9.07889 3.68399 10.1384C3.68191 10.1402 3.67982 10.1417 3.67773 10.1435C3.67833 10.1446 3.67863 10.1458 3.67893 10.147C3.68071 10.1461 3.6822 10.1446 3.68399 10.1435C4.03567 11.1892 4.72501 12.1158 5.62835 12.7482C6.19371 13.1473 6.84014 13.428 7.51995 13.5654C8.18545 13.6987 8.87927 13.6844 9.54477 13.5705C10.2103 13.4564 10.8519 13.209 11.4128 12.8338C11.4128 12.8338 11.413 12.834 11.4134 12.8344C11.4164 12.8326 11.4194 12.8308 11.4221 12.8287C12.3081 12.2294 12.9301 11.2691 13.1095 10.2147H8.48467V6.90178H8.48944V6.89701H16.4797C16.7458 8.38955 16.6889 9.94378 16.2657 11.4029L16.2663 11.4026Z" fill="white" />
      </svg>
    </div>
  );
}

export function PayPalIcon({ size = 32 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: '#003087' }}
    >
      <span className="text-white font-bold" style={{ fontSize: size * 0.38 }}>P</span>
    </div>
  );
}

export function VisaIcon({ size = 32 }: { size?: number }) {
  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: '#1A1F71', borderRadius: size * 0.2 }}
    >
      <svg width={size * 0.72} height={size * 0.234} viewBox="0 0 43 14" fill="none">
        <path d="M18.3629 13.5165H14.9302L17.0772 0.240662H20.5097L18.3629 13.5165Z" fill="white" />
        <path d="M30.806 0.565203C30.1289 0.296586 29.055 0 27.7271 0C24.3372 0 21.95 1.80767 21.9354 4.3921C21.9072 6.29889 23.6445 7.35796 24.9439 7.99375C26.2719 8.64342 26.7234 9.06747 26.7234 9.64656C26.7099 10.5359 25.6502 10.9459 24.6619 10.9459C23.2914 10.9459 22.5571 10.7345 21.4412 10.2397L20.9891 10.0276L20.5088 13.0077C21.3139 13.3745 22.7972 13.7 24.3372 13.7143C27.939 13.7143 30.2839 11.9346 30.3117 9.18047C30.3254 7.66919 29.4081 6.51119 27.4303 5.56493C26.2297 4.95749 25.4944 4.54791 25.4944 3.92639C25.5085 3.36137 26.1163 2.78265 27.4716 2.78265C28.5876 2.75431 29.4075 3.02255 30.0286 3.29098L30.3391 3.43195L30.806 0.565203Z" fill="white" />
        <path d="M35.3681 8.81332C35.6508 8.05064 36.7386 5.09886 36.7386 5.09886C36.7243 5.1272 37.0207 4.3221 37.1902 3.82785L37.4301 4.97178C37.4301 4.97178 38.0802 8.14956 38.2213 8.81332C37.6848 8.81332 36.0461 8.81332 35.3681 8.81332ZM39.6053 0.240662H36.9501C36.1313 0.240662 35.5092 0.480559 35.156 1.34216L30.0571 13.5163H33.659C33.659 13.5163 34.2519 11.8777 34.3794 11.5248C34.7745 11.5248 38.2784 11.5248 38.7867 11.5248C38.8853 11.9909 39.1963 13.5163 39.1963 13.5163H42.3747L39.6053 0.240662Z" fill="white" />
        <path d="M12.0629 0.240662L8.70119 9.29349L8.33383 7.45747C7.71232 5.33894 5.76311 3.03721 3.58789 1.89272L6.66713 13.5024H10.2971L15.6927 0.240662H12.0629Z" fill="white" />
        <path d="M5.57939 0.240662H0.0565013L0 0.508903C4.3082 1.61059 7.16143 4.26616 8.3337 7.45803L7.13309 1.35661C6.93542 0.508715 6.32798 0.268631 5.57939 0.240662Z" fill="white" />
      </svg>
    </div>
  );
}

export function MastercardIcon({ size = 32 }: { size?: number }) {
  const s = size * 0.8;
  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={s} height={s * 0.625} viewBox="0 0 48 30" fill="none">
        <circle cx="18" cy="15" r="14" fill="#EB001B" />
        <circle cx="30" cy="15" r="14" fill="#F79E1B" />
        <path d="M24 2.35A14 14 0 0 1 24 27.65A14 14 0 0 1 24 2.35Z" fill="#FF5F00" />
      </svg>
    </div>
  );
}

export function CardIcon({ size = 32 }: { size?: number }) {
  const markW = size * 0.56;
  const markH = markW * 0.65;
  const r = Math.max(2, markW * 0.1);
  const overlap = markW * 0.2;
  return (
    <div className="flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      {/* Visa mark */}
      <div
        style={{
          width: markW,
          height: markH,
          background: '#1A1F71',
          borderRadius: r,
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width={markW * 0.7} height={markH * 0.35} viewBox="0 0 43 14" fill="none">
          <path d="M18.3629 13.5165H14.9302L17.0772 0.240662H20.5097L18.3629 13.5165Z" fill="white" />
          <path d="M12.0629 0.240662L8.70119 9.29349L8.33383 7.45747C7.71232 5.33894 5.76311 3.03721 3.58789 1.89272L6.66713 13.5024H10.2971L15.6927 0.240662H12.0629Z" fill="white" />
          <path d="M5.57939 0.240662H0.0565013L0 0.508903C4.3082 1.61059 7.16143 4.26616 8.3337 7.45803L7.13309 1.35661C6.93542 0.508715 6.32798 0.268631 5.57939 0.240662Z" fill="white" />
          <path d="M30.806 0.565203C30.1289 0.296586 29.055 0 27.7271 0C24.3372 0 21.95 1.80767 21.9354 4.3921C21.9072 6.29889 23.6445 7.35796 24.9439 7.99375C26.2719 8.64342 26.7234 9.06747 26.7234 9.64656C26.7099 10.5359 25.6502 10.9459 24.6619 10.9459C23.2914 10.9459 22.5571 10.7345 21.4412 10.2397L20.9891 10.0276L20.5088 13.0077C21.3139 13.3745 22.7972 13.7 24.3372 13.7143C27.939 13.7143 30.2839 11.9346 30.3117 9.18047C30.3254 7.66919 29.4081 6.51119 27.4303 5.56493C26.2297 4.95749 25.4944 4.54791 25.4944 3.92639C25.5085 3.36137 26.1163 2.78265 27.4716 2.78265C28.5876 2.75431 29.4075 3.02255 30.0286 3.29098L30.3391 3.43195L30.806 0.565203Z" fill="white" />
          <path d="M35.3681 8.81332C35.6508 8.05064 36.7386 5.09886 36.7386 5.09886C36.7243 5.1272 37.0207 4.3221 37.1902 3.82785L37.4301 4.97178C37.4301 4.97178 38.0802 8.14956 38.2213 8.81332C37.6848 8.81332 36.0461 8.81332 35.3681 8.81332ZM39.6053 0.240662H36.9501C36.1313 0.240662 35.5092 0.480559 35.156 1.34216L30.0571 13.5163H33.659C33.659 13.5163 34.2519 11.8777 34.3794 11.5248C34.7745 11.5248 38.2784 11.5248 38.7867 11.5248C38.8853 11.9909 39.1963 13.5163 39.1963 13.5163H42.3747L39.6053 0.240662Z" fill="white" />
        </svg>
      </div>
      {/* Mastercard mark */}
      <div
        style={{
          width: markW,
          height: markH,
          background: '#252525',
          borderRadius: r,
          marginLeft: -overlap,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <svg width={markH * 0.85} height={markH * 0.6} viewBox="0 0 48 30" fill="none">
          <circle cx="18" cy="15" r="14" fill="#EB001B" />
          <circle cx="30" cy="15" r="14" fill="#F79E1B" />
          <path d="M24 2.35A14 14 0 0 1 24 27.65A14 14 0 0 1 24 2.35Z" fill="#FF5F00" />
        </svg>
      </div>
    </div>
  );
}

export function CoinifyIcon({ size = 18 }: { size?: number }) {
  return (
    <div
      className="rounded-full bg-white flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="text-black font-bold" style={{ fontSize: size * 0.55 }}>C</span>
    </div>
  );
}

export function ChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="var(--t-text-secondary)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronRight() {
  return (
    <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
      <path d="M1 1L7 7L1 13" stroke="var(--t-icon-stroke)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MenuIcon({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="hover:opacity-70 transition-opacity">
      <svg width="24" height="16" viewBox="0 0 24 16" fill="none">
        <path
          d="M22.4004 12V4C22.4004 2.67452 21.3255 1.59961 20 1.59961H6.7998V14.4004H20V16H4V14.4004H5.2002V1.59961H4C2.67452 1.59961 1.59961 2.67452 1.59961 4V12C1.59961 13.3255 2.67452 14.4004 4 14.4004V16L3.79395 15.9951C1.68056 15.8879 0 14.14 0 12V4C0 1.79086 1.79086 6.44266e-08 4 0H20C22.2091 0 24 1.79086 24 4V12L23.9951 12.2061C23.8913 14.2512 22.2512 15.8913 20.2061 15.9951L20 16V14.4004C21.3255 14.4004 22.4004 13.3255 22.4004 12Z"
          fill="var(--t-icon-stroke)"
        />
      </svg>
    </button>
  );
}

export function DarkModeIcon({ size = 40 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-surface)' }}
    >
      <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7.5" stroke="var(--t-text)" strokeWidth="1.5" />
        <path d="M10 2.5A7.5 7.5 0 0 1 10 17.5" fill="var(--t-text)" />
      </svg>
    </div>
  );
}

/* ───────────── V2 circular payment icons ───────────── */

export function PaymentBankIcon({ size = 20 }: { size?: number }) {
  const iconSize = size * 0.6;
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-payment-icon-bg)' }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 12 12" fill="none">
        <path d="M11 3.5H1L6 0.5L11 3.5Z" stroke="white" strokeWidth="0.8" />
        <rect x="1.5" y="4.5" width="0.8" height="5" fill="white" />
        <rect x="4.2" y="4.5" width="0.8" height="5" fill="white" />
        <rect x="6.9" y="4.5" width="0.8" height="5" fill="white" />
        <rect x="9.6" y="4.5" width="0.8" height="5" fill="white" />
        <rect x="0.5" y="10.5" width="11" height="0.5" fill="white" />
      </svg>
    </div>
  );
}

export function PaymentCardsIcon({ size = 20 }: { size?: number }) {
  const iconSize = size * 0.6;
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-payment-icon-bg)' }}
    >
      <svg width={iconSize} height={iconSize * 0.75} viewBox="0 0 12 9" fill="none">
        <rect x="0.5" y="0.5" width="11" height="8" rx="1.5" stroke="white" strokeWidth="0.8" fill="none" />
        <rect x="0.5" y="2.5" width="11" height="1.5" fill="white" />
        <rect x="2" y="5.5" width="3" height="1" rx="0.5" fill="white" />
      </svg>
    </div>
  );
}

export function PaymentAppleIcon({ size = 20 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-payment-icon-bg)' }}
    >
      <svg width={10} height={12} viewBox="0 0 10 12" fill="none">
        <path d="M8.32 6.32C8.31 5.1 9.32 4.5 9.37 4.47C8.77 3.59 7.83 3.47 7.5 3.46C6.73 3.38 5.99 3.93 5.6 3.93C5.2 3.93 4.59 3.47 3.94 3.48C3.1 3.5 2.32 3.99 1.89 4.74C1.01 6.27 1.66 8.52 2.5 9.76C2.93 10.37 3.43 11.05 4.08 11.03C4.71 11 4.95 10.63 5.71 10.63C6.46 10.63 6.69 11.03 7.34 11.02C8.02 11 8.45 10.4 8.87 9.79C9.36 9.09 9.56 8.41 9.57 8.37C9.55 8.37 8.33 7.88 8.32 6.32Z" fill="white" />
        <path d="M7.13 2.57C7.49 2.12 7.74 1.5 7.67 0.88C7.15 0.91 6.51 1.24 6.13 1.68C5.79 2.08 5.5 2.72 5.58 3.32C6.16 3.37 6.76 3.02 7.13 2.57Z" fill="white" />
      </svg>
    </div>
  );
}

export function PaymentGoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{ width: size, height: size, background: 'var(--t-payment-icon-bg)' }}
    >
      <svg width={10} height={10} viewBox="0 0 10 10" fill="none">
        <path d="M9.68 5.11C9.68 4.76 9.65 4.42 9.6 4.09H5V6.02H7.62C7.52 6.58 7.2 7.05 6.73 7.37V8.5H8.26C9.14 7.69 9.68 6.51 9.68 5.11Z" fill="white" />
        <path d="M5 9.58C6.28 9.58 7.35 9.14 8.26 8.5L6.73 7.37C6.33 7.64 5.81 7.79 5 7.79C3.78 7.79 2.73 6.97 2.38 5.87H0.8V7.04C1.71 8.84 3.22 9.58 5 9.58Z" fill="white" />
        <path d="M2.38 5.87C2.28 5.57 2.22 5.25 2.22 4.93C2.22 4.6 2.28 4.28 2.38 3.98V2.82H0.8C0.46 3.49 0.27 4.24 0.27 5.03C0.27 5.73 0.44 6.4 0.73 7L2.38 5.87Z" fill="white" />
        <path d="M5 2.07C5.74 2.07 6.39 2.32 6.91 2.82L8.29 1.45C7.35 0.57 6.28 0.07 5 0.07C3.22 0.07 1.71 1.14 0.8 2.82L2.38 3.98C2.73 2.88 3.78 2.07 5 2.07Z" fill="white" />
      </svg>
    </div>
  );
}

/* ───────────── Additional V2 icons ───────────── */

export function NRLogoIcon({ size = 24 }: { size?: number }) {
  const w = size * (46 / 40);
  return (
    <svg width={w} height={size} viewBox="0 0 48 32" fill="none" preserveAspectRatio="xMidYMid meet">
      <path d="M0 0H5.76C11.0619 0 15.36 4.47715 15.36 10V32H0V0Z" fill="var(--t-text)" />
      <path d="M16.32 0H22.08C27.3819 0 31.68 4.47715 31.68 10V32H16.32V0Z" fill="var(--t-text)" />
      <path d="M32.64 8C32.64 3.58172 36.0785 0 40.32 0C44.5615 0 48 3.58172 48 8C48 12.4183 44.5615 16 40.32 16C36.0785 16 32.64 12.4183 32.64 8Z" fill="var(--t-text)" />
    </svg>
  );
}

export function InfoIcon({ size = 12 }: { size?: number }) {
  return (
    <div
      className="flex items-center justify-center rounded-full shrink-0"
      style={{
        width: size,
        height: size,
        background: 'var(--t-text-secondary)',
      }}
    >
      <span
        style={{
          fontSize: size * 0.75,
          fontWeight: 500,
          lineHeight: 1,
          letterSpacing: '0.18px',
          color: 'var(--t-bg)',
        }}
      >
        i
      </span>
    </div>
  );
}

export function MoreDotsIcon({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
      style={{ width: 40, height: 40 }}
    >
      <svg width="20" height="4" viewBox="0 0 20 4" fill="none">
        <circle cx="2" cy="2" r="2" fill="var(--t-text)" />
        <circle cx="10" cy="2" r="2" fill="var(--t-text)" />
        <circle cx="18" cy="2" r="2" fill="var(--t-text)" />
      </svg>
    </button>
  );
}

export function SearchIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5.25" stroke="var(--t-text-secondary)" strokeWidth="1.2" />
      <path d="M11 11l3.5 3.5" stroke="var(--t-text-secondary)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function ArrowUpIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path d="M6 10V2M6 2L2.5 5.5M6 2l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CheckmarkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M11.5 3.5L5.5 10.5L2.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function XIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function getPaymentIcon(type: string, size?: number, theme?: 'dark' | 'light'): React.ReactNode;
export function getPaymentIcon(type: string, size = 32, _theme?: 'dark' | 'light'): React.ReactNode {
  switch (type) {
    case 'bank': return <PaymentBankIcon size={size} />;
    case 'card': return <PaymentCardsIcon size={size} />;
    case 'applepay': return <PaymentAppleIcon size={size} />;
    case 'googlepay': return <PaymentGoogleIcon size={size} />;
    case 'revolut': return <RevolutIcon size={size} />;
    case 'paypal': return <PayPalIcon size={size} />;
    case 'visa': return <VisaIcon size={size} />;
    case 'mastercard': return <MastercardIcon size={size} />;
    default: return <GenericCoinIcon size={size} />;
  }
}
